import { useInstanceStore } from '@/store';
import type {
  BaseInstance,
  IBaseInstanceConfig,
} from '@pigello/pigello-matrix';
import { isEqual } from 'lodash';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { DefaultValues, SetValueConfig } from 'react-hook-form';
import { useForm as useHookForm } from 'react-hook-form';
import { useDeepCompareEffect } from '../use-deep-compare-effect';
import type { DynamicField, OrderedFields } from './types';

type UseFormProps<T extends BaseInstance, I> = {
  isUpdate?: boolean;
  disabled?: boolean;
  config: IBaseInstanceConfig<T>;
  preMutatedValues?: Partial<T>;
  defaultValues: DefaultValues<T>;
  values?: Partial<T>;
  persistedInstanceId?: string;
  orderedFields?: Array<{
    key: I;
    fields: Array<keyof T>;
  }>;
};

export const isEmptyObject = (args: unknown): boolean => {
  if (!args || typeof args !== 'object' || Array.isArray(args)) {
    return false;
  }
  return Object.keys(args).length === 0;
};

export function useForm<T extends BaseInstance, I extends string = string>({
  isUpdate = false,
  disabled = false,
  config,
  preMutatedValues = {},
  defaultValues,
  values,
  orderedFields,
  persistedInstanceId,
}: UseFormProps<T, I>) {
  const pathname = usePathname();
  const hasSetPreMutatedValue = useRef(false);
  const dirtyFieldsRef = useRef<Partial<T>>({});
  const {
    addInstance,
    updateInstance,
    hasInstance,
    getInstance,
    removeInstance,
  } = useInstanceStore();

  const zIdentifier = useMemo(() => {
    let id = persistedInstanceId ?? `${pathname}-${config.modelName}`;

    if (!isUpdate || persistedInstanceId) {
      return id;
    }

    if (values?.id != null && values.id.toString().length > 0) {
      id += `-${values.id}`;
    }
    return id;
  }, [pathname, config.modelName, values, isUpdate, persistedInstanceId]);

  const zIdentifierRef = useRef(zIdentifier);

  useEffect(() => {
    zIdentifierRef.current = zIdentifier;
  }, [zIdentifier]);

  const hasStoredInstance = useMemo(() => {
    if (!zIdentifier) {
      return false;
    }
    return hasInstance(zIdentifier);
  }, [zIdentifier, hasInstance]);

  const form = useHookForm<T | Partial<T>>({
    defaultValues,
    shouldFocusError: true,
    values,
    resetOptions: {
      keepDirtyValues: true,
    },
    disabled,
  });

  const { setValue, watch, getValues } = form;
  const { dirtyFields, isReady } = form.formState;

  const setFormValue = useCallback(
    <const K extends keyof T>(
      name: K,
      value: T[K] | null,
      options: SetValueConfig = { shouldDirty: true }
    ) => {
      setValue(name as any, value as any, options);
    },
    [setValue]
  );

  const setMultipleFormValues = useCallback(
    (values: Partial<T>, options: SetValueConfig = { shouldDirty: true }) => {
      for (const [field, value] of Object.entries(values)) {
        setValue(field as any, value as any, options);
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (!hasStoredInstance || !zIdentifier || isUpdate) {
      return;
    }

    const savedValues = getInstance(zIdentifier);

    if (!savedValues) {
      console.warn(
        'The instance should exists in zustand. But was unable to fetch it.',
        zIdentifier
      );

      return;
    }
    setMultipleFormValues(savedValues as Partial<T>);
  }, [
    hasStoredInstance,
    getInstance,
    zIdentifier,
    isUpdate,
    setMultipleFormValues,
  ]);

  useDeepCompareEffect(() => {
    if (hasSetPreMutatedValue.current) {
      return;
    }
    if (!isEmptyObject(preMutatedValues) && isReady) {
      for (const [key, value] of Object.entries(preMutatedValues)) {
        const currentValue = getValues(key as any);
        setValue(key as any, value, {
          shouldDirty: !isEqual(currentValue, value),
        });
      }
      hasSetPreMutatedValue.current = true;
    }
  }, [preMutatedValues, setValue, isReady, getValues]);

  const getDirtyData = useCallback(
    (dirtyFields: Array<keyof T>) => {
      const currentValues = getValues();
      return dirtyFields.reduce(
        (acc, field) => {
          acc[field] = currentValues[field];
          return acc;
        },
        {} as T | Partial<T>
      );
    },
    [getValues]
  );

  const checkStore = useCallback(
    (dirtyInstance: Partial<T>) => {
      if (!zIdentifierRef.current) {
        return false;
      }
      if (!hasInstance(zIdentifierRef.current)) {
        addInstance(zIdentifierRef.current, config.modelName, dirtyInstance);
        return true;
      }
      return false;
    },
    [addInstance, hasInstance, config.modelName]
  );

  const handleDirtyFields = useCallback(
    (currentDirty: Partial<T>) => {
      dirtyFieldsRef.current = currentDirty;

      if (!zIdentifierRef.current) {
        return;
      }

      if (Object.keys(currentDirty).length === 0) {
        removeInstance(zIdentifierRef.current);
        return;
      }

      if (!checkStore(currentDirty)) {
        updateInstance(zIdentifierRef.current, currentDirty);
      }
    },
    [checkStore, removeInstance, updateInstance]
  );

  const handleFormUpdate = useCallback(() => {
    const dirtyData = getDirtyData(Object.keys(dirtyFields) as Array<keyof T>);
    if (isEqual(dirtyFieldsRef.current, dirtyData)) {
      return;
    }

    handleDirtyFields(dirtyData);
  }, [dirtyFields, getDirtyData, handleDirtyFields]);

  useEffect(() => {
    const { unsubscribe } = watch(() => {
      handleFormUpdate();
    });

    return () => unsubscribe();
  }, [handleFormUpdate, watch]);

  const fields = useMemo(
    () =>
      (Object.keys(defaultValues) as Array<keyof T>)
        .map((field) => ({
          [field]: {
            name: field,
            required: config.fields[field].required,
            label: config.fields[field].verboseName,
            description: config.fields[field]?.description,
            control: form.control,
            type: config.fields[field].type,
            readOnly: config.fields[field].readOnly,
            nullable: config.fields[field].nullable,
            config,
          },
        }))
        .reduce((acc, field) => ({ ...acc, ...field }), {}),
    [config, defaultValues, form.control]
  );

  const chapterFields = useMemo(
    () =>
      orderedFields
        ?.map((chapter) => ({
          [chapter.key]: chapter.fields.map((field) => ({
            name: field,
            required: config.fields[field].required,
            label: config.fields[field].verboseName,
            description: config.fields[field]?.description,
            control: form.control,
            type: config.fields[field].type,
            readOnly: config.fields[field].readOnly,
            nullable: config.fields[field].nullable,
            config,
          })),
        }))
        .reduce((acc, chapter) => ({ ...acc, ...chapter }), {} as const) ?? {},
    [config, form.control, orderedFields]
  ) as OrderedFields<I, T>;

  return {
    ...form,
    setFormValue,
    setMultipleFormValues,
    fields: fields as DynamicField<T>,
    orderedFields: chapterFields,
    getDirtyData: () =>
      getDirtyData(Object.keys(dirtyFields) as Array<keyof typeof values>),
  };
}
